import { Fragment, useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import api, { formatMYR, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Trash2, Receipt, ChevronDown, ChevronRight, FileDown, Download, Truck, Phone } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import DateRangePicker, { toIsoDate } from "../components/DateRangePicker";
import { downloadInvoicePDF } from "../lib/invoicePdf";
import { exportSalesCSV } from "../lib/exportCsv";
import BraceletDesignPanel from "../components/BraceletDesignPanel";

const PAYMENT_OPTIONS = ["Shopee", "Lazada", "Tiktok", "Debit Card/Credit Card", "Paypal", "Online Transfer"];
const ALL = "__all__";

const STATUS_LABELS = {
    completed: { label: "已完成", cls: "bg-gold-100 text-gold-800" },
    pending: { label: "待处理", cls: "bg-amber-100 text-amber-800" },
    to_collect: { label: "待自取", cls: "bg-blue-100 text-blue-800" },
    booked: { label: "已预订", cls: "bg-purple-100 text-purple-800" },
    payment_pending: { label: "待付款", cls: "bg-yellow-100 text-yellow-800" },
    confirmed: { label: "已确认", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "已取消", cls: "bg-slate-200 text-slate-600 line-through" },
};

const STATUS_ORDER = ["payment_pending", "confirmed", "pending", "to_collect", "booked", "completed", "cancelled"];

export default function SalesPage() {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [range, setRange] = useState(undefined);
    const [paymentFilter, setPaymentFilter] = useState(ALL);
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") === "pending" ? "pending" : ALL);
    const [downloading, setDownloading] = useState(null);
    const [footerNote, setFooterNote] = useState("");
    const { user } = useAuth();
    const navigate = useNavigate();

    // Sync q ↔ URL so the command palette can deep-link
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        if (urlQ !== q) setQ(urlQ);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        const next = new URLSearchParams(searchParams);
        if (q) next.set("q", q); else next.delete("q");
        if ((q || "") !== urlQ) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    useEffect(() => {
        api.get("/settings").then(({ data }) => setFooterNote(data.invoice_footer_note || "")).catch(() => {});
    }, []);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (range?.from) params.start_date = toIsoDate(range.from);
        if (range?.to) params.end_date = toIsoDate(range.to);
        else if (range?.from) params.end_date = toIsoDate(range.from);
        if (paymentFilter && paymentFilter !== ALL) params.payment_method = paymentFilter;
        if (statusFilter && statusFilter !== ALL) params.order_status = statusFilter;
        const { data } = await api.get("/sales", { params });
        setSales(data);
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, paymentFilter, statusFilter]);

    const paymentChoices = useMemo(() => {
        const set = new Set(PAYMENT_OPTIONS);
        sales.forEach((s) => { if (s.payment_method) set.add(s.payment_method); });
        return Array.from(set);
    }, [sales]);

    const del = async (s) => {
        if (!window.confirm(`确定删除发票 ${s.invoice_no}？库存将恢复。`)) return;
        try {
            await api.delete(`/sales/${s.id}`);
            toast.success("销售记录已删除");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "删除失败");
        }
    };

    const changeStatus = async (s, newStatus) => {
        if (newStatus === s.order_status) return;
        try {
            await api.put(`/sales/${s.id}/status`, { order_status: newStatus });
            toast.success(`已更新为 ${STATUS_LABELS[newStatus]?.label || newStatus}`);
            setSales((prev) => prev.map((x) => (x.id === s.id ? { ...x, order_status: newStatus } : x)));
        } catch (err) {
            toast.error(err.response?.data?.detail || "更新失败");
        }
    };

    const exportPdf = async (s) => {
        setDownloading(s.id);
        try {
            await downloadInvoicePDF(s, user, { footerNote });
            toast.success(`已导出 ${s.invoice_no}.pdf`);
        } catch (e) {
            toast.error("导出 PDF 失败");
        } finally {
            setDownloading(null);
        }
    };

    const filtered = useMemo(() => sales.filter((s) => {
        if (!q) return true;
        const t = q.toLowerCase();
        return s.invoice_no.toLowerCase().includes(t)
            || (s.customer_name || "").toLowerCase().includes(t)
            || (s.customer_phone || "").toLowerCase().includes(t);
    }), [sales, q]);

    const totalRevenue = filtered.reduce((sum, s) => sum + s.total, 0);

    return (
        <div className="space-y-6" data-testid="sales-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">交易</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">销售记录</h1>
                    <p className="text-sm text-gold-500 mt-1">所有已完成的销售单</p>
                </div>
                <Button data-testid="new-sale-page-btn" onClick={() => navigate("/sales/new")} className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                    <Plus className="h-4 w-4" /> 新建销售单
                </Button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button data-testid="export-sales-btn" variant="outline" className="gap-2">
                            <Download className="h-4 w-4" /> 导出
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => exportSalesCSV(filtered, "invoice")}>
                            CSV · 发票汇总（每单一行）
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => exportSalesCSV(filtered, "line")}>
                            CSV · 明细（每件商品一行）
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">显示订单数</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{filtered.length}</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">显示营收</div>
                    <div className="text-2xl font-bold font-mono text-gold-600 mt-2">{formatMYR(totalRevenue)}</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">平均订单额</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{formatMYR(filtered.length ? totalRevenue / filtered.length : 0)}</div>
                </Card>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="sales-search-input"
                            placeholder="搜索发票号、客户名、电话..."
                            className="pl-9"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                    <DateRangePicker value={range} onChange={setRange} />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="status-filter-select" className="w-[180px] h-10">
                            <SelectValue placeholder="订单状态" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>全部状态</SelectItem>
                            {STATUS_ORDER.map((k) => (
                                <SelectItem key={k} value={k}>{STATUS_LABELS[k].label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                        <SelectTrigger data-testid="payment-filter-select" className="w-[200px] h-10">
                            <SelectValue placeholder="按付款方式筛选" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>全部付款方式</SelectItem>
                            {paymentChoices.map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {loading ? (
                    <div className="p-12 text-center text-gold-500">加载中...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-sm text-gold-500">{sales.length === 0 ? "还没有销售记录" : "没有匹配的记录"}</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-gold-50/60">
                                    <th className="w-10"></th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">发票号</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">客户</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">日期</th>
                                    <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">状态</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">商品数</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">总额</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((s) => {
                                    const isOpen = expanded[s.id];
                                    const status = s.order_status || "completed";
                                    const statusMeta = STATUS_LABELS[status] || STATUS_LABELS.completed;
                                    const hasDelivery = s.delivery_method || s.delivery_address || s.tracking_no;
                                    return (
                                        <Fragment key={s.id}>
                                            <tr data-testid={`sale-row-${s.id}`} className="border-b border-slate-100 hover:bg-gold-50 transition-colors duration-150">
                                                <td className="pl-4">
                                                    <button onClick={() => setExpanded({ ...expanded, [s.id]: !isOpen })} className="text-slate-400 hover:text-slate-700">
                                                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                    </button>
                                                </td>
                                                <td className="py-3 px-4 text-sm font-mono font-semibold text-slate-900">
                                                    <div className="flex items-center gap-1.5">
                                                        {s.invoice_no}
                                                        {hasDelivery && <Truck className="h-3 w-3 text-slate-400" title="有配送信息" />}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 text-sm text-slate-700">
                                                    <div>{s.customer_name}</div>
                                                    {s.customer_phone && (
                                                        <div className="text-xs text-gold-500 font-mono inline-flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {s.customer_phone}</div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-slate-600 font-mono">{formatDate(s.created_at)}</td>
                                                <td className="py-3 px-4 text-center">
                                                    <Select value={status} onValueChange={(v) => changeStatus(s, v)}>
                                                        <SelectTrigger data-testid={`status-select-${s.id}`} className={`h-7 w-[110px] mx-auto text-xs border-0 px-2 ${statusMeta.cls}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {STATUS_ORDER.map((k) => (
                                                                <SelectItem key={k} value={k}>{STATUS_LABELS[k].label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </td>
                                                <td className="py-3 px-4 text-sm text-right font-mono text-slate-700">{s.items.reduce((n, it) => n + it.quantity, 0)}</td>
                                                <td className="py-3 px-4 text-sm text-right font-mono font-bold text-gold-600">{formatMYR(s.total)}</td>
                                                <td className="py-3 px-4 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            data-testid={`export-pdf-${s.id}`}
                                                            variant="ghost"
                                                            size="sm"
                                                            disabled={downloading === s.id}
                                                            onClick={() => exportPdf(s)}
                                                            className="text-slate-600 hover:text-gold-700 hover:bg-gold-50 gap-1.5"
                                                            title="导出 PDF 发票"
                                                        >
                                                            <FileDown className="h-3.5 w-3.5" />
                                                            <span className="hidden sm:inline text-xs">PDF</span>
                                                        </Button>
                                                        <Button data-testid={`delete-sale-${s.id}`} variant="ghost" size="sm" onClick={() => del(s)} className="text-red-600 hover:bg-red-50">
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isOpen && (
                                                <tr className="bg-gold-50/40">
                                                    <td colSpan={8} className="px-14 py-4">
                                                        <div className="border border-slate-200 rounded-md bg-white">
                                                            <table className="w-full">
                                                                <thead>
                                                                    <tr className="border-b border-slate-200">
                                                                        <th className="text-left py-2 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">产品</th>
                                                                        <th className="text-left py-2 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">SKU</th>
                                                                        <th className="text-right py-2 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">数量</th>
                                                                        <th className="text-right py-2 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">单价</th>
                                                                        <th className="text-right py-2 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">小计</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {s.items.map((it, i) => (
                                                                        <tr key={`${it.product_id}-${i}`} className="border-b border-slate-100 last:border-0">
                                                                            <td className="py-2 px-4 text-sm text-slate-900">{it.product_name}</td>
                                                                            <td className="py-2 px-4 text-sm text-slate-600 font-mono">{it.sku}</td>
                                                                            <td className="py-2 px-4 text-sm text-right font-mono">{it.quantity}</td>
                                                                            <td className="py-2 px-4 text-sm text-right font-mono">{formatMYR(it.unit_price)}</td>
                                                                            <td className="py-2 px-4 text-sm text-right font-mono">{formatMYR(it.line_total)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                            {hasDelivery && (
                                                                <div className="px-4 py-3 border-t border-slate-200 bg-blue-50/40">
                                                                    <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1.5 inline-flex items-center gap-1"><Truck className="h-3 w-3" /> 配送信息</div>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
                                                                        {s.delivery_method && <div>配送方式：<span className="text-slate-900 font-medium">{s.delivery_method}</span></div>}
                                                                        {s.tracking_no && <div>快递单号：<span className="text-slate-900 font-medium font-mono">{s.tracking_no}</span></div>}
                                                                        {s.delivery_contact && <div>联系人：<span className="text-slate-900 font-medium">{s.delivery_contact}</span></div>}
                                                                        {s.delivery_address && <div className="md:col-span-2">地址：<span className="text-slate-900 font-medium">{s.delivery_address}</span></div>}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {s.note && (
                                                                <div className="px-4 py-2 border-t border-slate-200 text-xs text-gold-500">
                                                                    备注：{s.note}
                                                                </div>
                                                            )}
                                                            {(s.platform || s.payment_method || s.discount_percent > 0) ? (
                                                                <div className="px-4 py-2 border-t border-slate-200 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gold-500">
                                                                    {s.platform && <span>平台：<span className="text-slate-800 font-medium">{s.platform}</span></span>}
                                                                    {s.payment_method && <span>付款方式：<span className="text-slate-800 font-medium">{s.payment_method}</span></span>}
                                                                    {s.discount_percent > 0 && <span>折扣百分比：<span className="text-slate-800 font-medium">{s.discount_percent}%</span></span>}
                                                                </div>
                                                            ) : null}
                                                            {s.bracelet_design && (
                                                                <BraceletDesignPanel design={s.bracelet_design} />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
